import { useState } from 'react';
import { api } from '../lib/api.js';
import { areaLabel, areasInMap, mergeAreas } from '../lib/store-check.js';

// An owner's decisions about one camera: what it is called, whether it faces
// customers, the area map the AI reads against, which areas' statuses are
// shown, and whether the camera is used at all. Nothing is read from a camera
// until an owner switches it on here.

const input = { width: '100%', fontSize: 13, padding: '7px 9px', border: '1px solid var(--rule, #ddd)', borderRadius: 6, boxSizing: 'border-box' };
const label = { fontSize: 11, fontWeight: 600, color: '#616161', display: 'block', marginBottom: 3 };

export function StoreCheckCameraEditor({ camera, picture, onDone, onCancel }) {
  const drafted = !camera.area_map && Boolean(camera.area_map_draft);
  const [name, setName] = useState(camera.name || camera.recorder_name || '');
  const [sees, setSees] = useState(camera.sees || '');
  const [faces, setFaces] = useState(Boolean(camera.faces_customers));
  const [approx, setApprox] = useState(Boolean(camera.counts_approximate));
  const [map, setMap] = useState(camera.area_map || camera.area_map_draft || '');
  const [areas, setAreas] = useState(() => mergeAreas(areasInMap(camera.area_map || camera.area_map_draft || ''), camera.areas));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function editMap(text) { setMap(text); setAreas((was) => mergeAreas(areasInMap(text), was)); }
  function toggle(areaName) { setAreas((was) => was.map((a) => (a.name === areaName ? { ...a, show_status: !a.show_status } : a))); }

  async function save(status) {
    setSaving(true); setError('');
    try {
      await api('PATCH', `/api/store-check/cameras/${camera.id}`, {
        name: name.trim(), sees: sees.trim(), faces_customers: faces, counts_approximate: approx, area_map: map, areas, status,
      });
      onDone();
    } catch (e) { setError(e.message || 'Could not save'); setSaving(false); }
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-camera-editor={camera.id}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{camera.store} · camera {camera.channel + 1}</div>
      {picture && <img src={picture} alt="What this camera sees" style={{ width: '100%', maxWidth: 520, borderRadius: 8, background: '#eee' }} />}
      <div>
        <label style={label} htmlFor="sc-name">Name</label>
        <input id="sc-name" style={input} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <label style={label} htmlFor="sc-sees">What it sees, in a sentence</label>
        <input id="sc-sees" style={input} value={sees} maxLength={300} onChange={(e) => setSees(e.target.value)} />
      </div>
      <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" checked={faces} onChange={(e) => setFaces(e.target.checked)} /> It faces customers (count people in line and seated)
      </label>
      <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" checked={approx} onChange={(e) => setApprox(e.target.checked)} /> Its counts are approximate (far from the customers)
      </label>
      <div>
        <label style={label} htmlFor="sc-map">Area map — one line per area, like “- front_counter: the counter at the bottom left”</label>
        {drafted && <div className="text-xs text-gray-500" style={{ marginBottom: 4 }}>Drafted by the AI from this camera’s picture. Check every line against the picture before switching the camera on.</div>}
        <textarea id="sc-map" style={{ ...input, minHeight: 150, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} value={map} onChange={(e) => editMap(e.target.value)} />
      </div>
      <div>
        <div style={label}>Show a status for these areas</div>
        {areas.length === 0 && <div className="text-xs text-gray-500">No areas yet. Add lines to the area map.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {areas.map((a) => (
            <label key={a.name} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={a.show_status} onChange={() => toggle(a.name)} /> {areaLabel(a.name)}
            </label>
          ))}
        </div>
        <div className="text-xs text-gray-500" style={{ marginTop: 4 }}>An area left unticked is still in the picture, but the AI’s status for it is not shown.</div>
      </div>
      {error && <div style={{ color: '#b00020', fontSize: 13 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn-primary" disabled={saving || !name.trim()} onClick={() => save('in_use')}>{camera.status === 'in_use' ? 'Save' : 'Switch on'}</button>
        <button className="btn-secondary" disabled={saving || !name.trim()} onClick={() => save('never_use')}>Never use</button>
        {camera.status === 'found' && <button className="btn-secondary" disabled={saving || !name.trim()} onClick={() => save('found')}>Save, decide later</button>}
        <button className="btn-secondary" disabled={saving} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
